import {
  createApiClientFromParams,
  createUnauthenticatedClient,
  OneGrepApiClient,
  OneGrepApiHighLevelClient,
} from "@toolprint/sdk";
import { Config } from "./config/schemas.js";
import { logger } from "./logger.js";

/**
 * Validate an API URL by checking if the health check endpoint is reachable.
 * @param url The API URL to validate
 * @returns True if the API URL is valid, false otherwise
 */
export async function validateApiUrl(url: string): Promise<boolean> {
  try {
    const client = createUnauthenticatedClient(url);

    // TODO: High level client health check seems broken.
    const response = await client.get({
      responseType: "json",
      url: "/health",
    });
    if (response.status !== 200) {
      logger.debug(
        `Health check failed with status ${response.status} for API URL: ${url}`,
      );
      return false;
    }

    logger.debug(`Health check passed for API URL: ${url}`);
    return true;
  } catch (error) {
    logger.debug(`Unable to validate API URL: ${url} ${JSON.stringify(error)}`);
    return false;
  }
}

/**
 * Create a raw API Client from a Config object.
 * @param config The config to use to create the client
 * @returns A an initialized OneGrepApiClient
 */
export function clientFromConfig(config: Config): OneGrepApiClient {
  try {
    // Check if API URL is set
    if (!config.api.url) {
      throw new Error(
        "API URL is not set. Please set an API URL using the account setup command.",
      );
    }

    const clientParams = {
      baseUrl: config.api.url,
      accessToken: config.credentials?.accessToken,
      apiKey: config.credentials?.apiKey,
    };

    logger.debug(`Creating client pointing to ${clientParams.baseUrl}`);

    return createApiClientFromParams(clientParams);
  } catch (error) {
    logger.debug(`Failed to create API client: ${error}`);
    throw error;
  }
}

export function bearerAuthClientFromConfig(
  config: Config,
): OneGrepApiHighLevelClient {
  return new OneGrepApiHighLevelClient(clientFromConfig(config));
}

export async function apiKeyFromConfig(config: Config): Promise<string> {
  // First check environment variable
  if (!!process.env.ONEGREP_API_KEY) {
    logger.debug("Using API key from environment variable");
    return process.env.ONEGREP_API_KEY;
  }

  // Check if API key is already in credentials (from X-API-KEY header)
  if (config.credentials?.apiKey) {
    logger.debug("Using API key from credentials");
    return config.credentials.apiKey;
  }

  // Otherwise, try to get API key from account information using bearer auth
  logger.debug("Fetching API key from account information using bearer auth", {
    hasAccessToken: !!config.credentials?.accessToken,
    apiUrl: config.api?.url,
  });

  // This should never happen - if we're trying to fetch API key via bearer auth without an access token
  if (!config.credentials?.accessToken) {
    logger.error(
      "Attempting to fetch API key via bearer auth without access token - this will fail",
    );
    throw new Error(
      "Cannot fetch API key: no access token available for bearer authentication",
    );
  }

  const userClient = bearerAuthClientFromConfig(config);
  const userAccount = await userClient.getAccountInformation();

  if (!userAccount) {
    throw new Error("No account information found");
  }
  logger.debug(`Using API key for user: ${userAccount.account.id}`);

  return userAccount.account.api_key;
}

export async function isClientAuthenticated(
  client: OneGrepApiClient,
): Promise<boolean> {
  const highLevelClient = new OneGrepApiHighLevelClient(client);
  const response = await highLevelClient.authStatus();
  return response.credentials_provided && response.is_authenticated;
}

export async function apiKeyClientFromConfig(
  config: Config,
): Promise<OneGrepApiClient> {
  const apiKey = await apiKeyFromConfig(config);

  return createApiClientFromParams({
    baseUrl: config.api.url,
    apiKey: apiKey,
  });
}
